import { useState } from 'react'

import './Quote.css'
import PricingSection from './PricingSection'
import Footnotes from './Footnotes'

import PricingTotals from './PricingTotals'

// Javascript Files
import PayrollServices from './PayrollServices'
import ProductDescriptions from './ProductDescriptions'
import ThingsYouNeedToKnow from './ThingsYouNeedToKnow'
import ImpDescriptions from './ImplementationDescriptions'
import EORServices from './EORServices'
//@ts-ignore
import { timeClockDetail } from '../common/types/types'
import { addDisclaimerSymbolsToLineItems } from './utils/disclaimerUtils.js'
import { VolumeDiscountGraph } from './VolumeDiscountGraph'
import ACAServices from './ACAServices'
import HRISServices from './HRISServices'

interface QuoteContentProps {
   getString: (key: string, fallback?: string) => string
   getFormattedString: (
      key: string,
      variables?: Record<string, string | number>,
      fallback?: string,
   ) => string
   PDFQuoteData: any
   formType: number
}

export default function QuoteContent({
   getString,
   getFormattedString,
   PDFQuoteData,
   formType,
}: QuoteContentProps) {
   const quoteData = PDFQuoteData

   let monthlyLineItems = []
   if (!quoteData?.IsForeignCurrencyQuote) {
      monthlyLineItems = [
         quoteData.HRHeaderRow,
         quoteData.ProductCoreRow,
         quoteData.ProductBenefitsAdministrationRow,
         quoteData.ProductPerformanceRow,
         quoteData.ProductTimeTrackingRow,
         quoteData.ProductTimeClockMonthlySupportRow,
         quoteData.RelatedEntityDiscountRow,
         quoteData.ProductAdditionalJobsRow,
         quoteData.ProductAdditionalStorageRow,
         quoteData.ProductTestAccountRow,
         ...quoteData.MonthlyDynamicProductRows,
         quoteData.PayrollHeaderRow,
         quoteData.MonthlyTaxRow,
         ...quoteData.AllPayrollRows,
         ...(quoteData.PayrollDynamicProductRows ?? []),
         quoteData.MonthlyTotalRow,
      ]
   } else {
      monthlyLineItems = [
         quoteData.HRHeaderRow,
         quoteData.ProductCoreRow,
         quoteData.ProductBenefitsAdministrationRow,
         quoteData.ProductPerformanceRow,
         quoteData.ProductTimeTrackingRow,
         quoteData.ProductTimeClockMonthlySupportRow,
         quoteData.RelatedEntityDiscountRow,
         quoteData.ProductAdditionalJobsRow,
         quoteData.ProductAdditionalStorageRow,
         quoteData.ProductTestAccountRow,
         ...quoteData.MonthlyDynamicProductRows,
         quoteData.MonthlyTotalRow,
         quoteData.PayrollHeaderRow,
         quoteData.MonthlyTaxRow,
         ...quoteData.AllPayrollRows,
         ...(quoteData.PayrollDynamicProductRows ?? []),
         ...(quoteData.AlwaysUSDPayrollRows ?? []),
         quoteData.PayrollTotalRow,
      ]
   }

   // Combines summarized discounts from monthly and one time totals. This is used to add disclaimer symbols to both monthly and one time line items in the case where a discount applies to a monthly product, but displays a symbol on the one time line item, or vice versa.
   const combinedSummarizedDiscounts = quoteData.calculatedValues
      ? quoteData.calculatedValues.monthlyTotal.appliedDiscounts.concat(
           quoteData.calculatedValues.oneTimeTotal.appliedDiscounts,
        )
      : []

   // Keep the Product / List Price / (Effective PEPM) / Quantity columns
   // vertically aligned between the Monthly and One-Time tables regardless of
   // how many discount columns each table actually needs. We build a single
   // shared set of <col> widths that both tables use via their <colgroup>,
   // which, combined with `table-layout: fixed`, guarantees identical column
   // positions. Whichever table has fewer cells is padded with empty
   // (header-less) cells inserted before the Total column so the rows line
   // up with those columns.
   const monthlyHeaderCols = quoteData.MonthlyHeaderRow?.length ?? 0
   const oneTimeHeaderCols = quoteData.OneTimeHeaderRow?.length ?? 0
   const alignedColumnCount = Math.max(monthlyHeaderCols, oneTimeHeaderCols)

   const alignedColumnWidths: string[] = []
   if (alignedColumnCount > 0) {
      alignedColumnWidths.push(`${quoteData.productNameColumnWidth ?? 0}%`)
      alignedColumnWidths.push(`${quoteData.productLPColumnWidth ?? 0}%`)
      if (quoteData.hasPEPMCols) {
         alignedColumnWidths.push(`${quoteData.productPEPMColumnWidth ?? 0}%`)
      }
      alignedColumnWidths.push(`${quoteData.productQuantityColumnWidth ?? 0}%`)
      alignedColumnWidths.push(`${quoteData.productMonthsColumnWidth ?? 0}%`)
      // Fill any remaining columns between Months and Total with
      // discount-width cols (these may be empty / header-less when a table
      // has fewer discounts than the other).
      const fixedCols = alignedColumnWidths.length
      const middleCols = Math.max(0, alignedColumnCount - fixedCols - 1)
      for (let i = 0; i < middleCols; i++) {
         alignedColumnWidths.push(
            `${quoteData.productDiscountColumnWidth ?? 0}%`,
         )
      }
      alignedColumnWidths.push(`${quoteData.productTotalColumnWidth ?? 0}%`)
   }

   const alignedPadColumnStyle = {
      '--width': `${quoteData.productDiscountColumnWidth ?? 0}%`,
   } as React.CSSProperties

   return (
      <div className='mainWrapper' id='QuoteContent'>
         {/* <Header
          bhrEmployeeName={last_agent_name}
          bhrEmployeeEmail={last_agent_email}
          clientEmployeeName={opportunity_contact_name}
          clientName={account_name}
          clientEmployeeEmail={opportunity_contact_email}
          expirationDate={new Date(quoteData.QuoteExpirationDate+"T00:00:00-07:00").toLocaleDateString('en-US', {month: "long", day: "numeric", year: "numeric"})}
          formType={formType}
          requestNumber={requestNumber}
          showTopMargin={false}
          estimatedPrepaymentRange={quoteData.EstimatedPrepaymentCoverage}
          showEstimatedCoveragePeriod={quoteData.ShowEstimatedCoveragePeriod}
        /> */}
         <div className='productsServices avoid-break-before'>
            {quoteData.HasMonthlyServices && (
               <PricingSection
                  sectionName={'Monthly'}
                  numColumns={quoteData.NumColumns}
                  sectionHeaders={quoteData.MonthlyHeaderRow}
                  lineItems={addDisclaimerSymbolsToLineItems(
                     monthlyLineItems,
                     combinedSummarizedDiscounts,
                  )}
                  tableClass='tableClass2 nobreak avoid-break-before'
                  tableHeaderClass='tableHeader2'
                  targetColumnCount={alignedColumnCount}
                  padColumnStyle={alignedPadColumnStyle}
                  columnWidths={alignedColumnWidths}
               />
            )}
            {quoteData.HasOneTimeServices && (
               <PricingSection
                  sectionName={'One-Time'}
                  numColumns={quoteData.NumColumns}
                  sectionHeaders={quoteData.OneTimeHeaderRow}
                  lineItems={addDisclaimerSymbolsToLineItems(
                     [
                        quoteData.HRImplementationRow,
                        quoteData.BenAdminImplementationRow,
                        quoteData.ImplementationCountriesRow,
                        quoteData.PayrollImplementationRow,
                        quoteData.ProductHistoricalDataImportRow,
                        quoteData.ProductMarketplaceListingRow,
                        quoteData.ProductSponsorshipRow,
                        quoteData.ProductPaidServicesRow,
                        quoteData.AccountBalanceRow,
                        quoteData.ProfessionalServicesHeaderRow,
                        ...quoteData.ProfessionalServicesRow,
                        quoteData.ConsultingServicesHeaderRow,
                        ...quoteData.ConsultingServicesRow,
                        ...quoteData.OneTimeDynamicProductRows,
                        ...(quoteData.AlwaysUSDPayrollOneTimeHeaderRow.length >
                        0
                           ? [quoteData.AlwaysUSDPayrollOneTimeHeaderRow]
                           : []),
                        ...(quoteData.AlwaysUSDPayrollOneTimeRows ?? []),
                        quoteData.TimeClocksHeaderRow,
                        ...quoteData.TimeClocksProductRows,
                        quoteData.OneTimeTotalRow,
                     ],
                     combinedSummarizedDiscounts,
                  )}
                  //   style={{borderBottom:"7px solid #8CC63E"}}
                  tableClass='tableClass2 nobreak avoid-break-before'
                  tableHeaderClass='tableHeader2'
                  targetColumnCount={alignedColumnCount}
                  padColumnStyle={alignedPadColumnStyle}
                  columnWidths={alignedColumnWidths}
               />
            )}
         </div>
         <wbr />
         <div className='footnotesAndTotals nobreak'>
            <Footnotes
               getString={getString}
               getFormattedString={getFormattedString}
               hasPEPMCols={quoteData.hasPEPMCols}
               mixedOverUnder25={quoteData.MixedOverUnder25}
               comments={quoteData.Comments}
               allOverMin={quoteData.AllOverMin}
               bundleApplied={quoteData.ShowBundleCol}
               hasPayrollImp={
                  quoteData.PayrollImpType !== null &&
                  quoteData.PayrollImpType !== 'none' &&
                  quoteData.QuoteType !== 'monthly'
               }
               includePayrollImpInActivation={
                  quoteData.IncludePayrollImpInActivation
               }
               calculatedValues={quoteData.calculatedValues}
               payrollImpType={quoteData.PayrollImpType}
               currencyObject={quoteData.currencyObject}
               // TODO: EASYBUTTON Remove unnecessary parameters after going live
               termLength={quoteData.TermLength}
               hrEECount={quoteData.ProductCoreEECount}
               termContractPermissions={quoteData.TermContractFeature}
               userRole={quoteData.user_role}
               product_list={quoteData.product_list}
               isCoTerm={quoteData.IsCoTerm}
               coTermMonths={quoteData.IsCoTerm ? quoteData.TermLength : null}
               payrollFirstPayDate={quoteData.PayrollFirstPayDate}
               isATSOnly={quoteData.IsATSOnly}
            />
            <PricingTotals
               months={quoteData.MonthsSelected}
               nonPayrollItems={quoteData.TotalItems}
               discountItems={quoteData.TotalDiscountItems}
               payrollItems={quoteData.PayrollTotalItems}
               payrollDiscountItems={quoteData.PayrollTotalDiscountItems}
               grandTotalListPrice={
                  quoteData.MonthsSelected < 12
                     ? quoteData.MonthlyGrandTotalListPrice
                     : quoteData.AnnualGrandTotalListPrice
               }
               payrollListPrice={
                  quoteData.MonthsSelected >= 12
                     ? quoteData.PayrollCombinedAnnualListPrice
                     : quoteData.PayrollCombinedListPrice
               }
               grandTotal={
                  quoteData.MonthsSelected < 12
                     ? quoteData.MonthlyGrandTotal
                     : quoteData.AnnualGrandTotal
               }
               payrollGrandTotal={
                  quoteData.MonthsSelected >= 12
                     ? quoteData.PayrollCombinedAnnualTotalPrice
                     : quoteData.PayrollCombinedTotalPrice
               }
               activationItems={quoteData.ActivationTotalItems}
               activationGrandTotal={quoteData.ActivationPrice}
               shouldHaveThreeAsterisks={quoteData.MixedOverUnder25}
               hasMonthlyServices={quoteData.HasMonthlyServices}
               hasNonPayrollServices={quoteData.HasHRProducts}
               hasPayrollSelected={quoteData.HasPayrollSelected}
               // showEffectiveDiscount={quoteData.ShowEffectiveDiscountOnQuote}
               isProrated={quoteData.IsProrated}
               daysPaidInMonth={quoteData.DaysPaidInMonth}
               currencyObject={quoteData.currencyObject}
               currencyObjectUSD={quoteData.currencyObjectUSD}
               isForeignCurrencyQuote={quoteData.IsForeignCurrencyQuote}
            />
            <VolumeDiscountGraph
               contractLength={quoteData.ContractLength}
               productPEPMBaseCost={quoteData.ProductCoreRow?.pepm || 9.0}
               productMinimumCharge={
                  quoteData.ProductCoreRow?.minimumCharge || 100.0
               }
               basePrice={
                  quoteData.MonthlyGrandTotalListPrice /
                     quoteData.ProductCoreEECount || undefined
               }
               basePEPMPrice={
                  quoteData.MonthlyGrandTotalListPrice /
                     quoteData.ProductCoreEECount || undefined
               }
               termContractFeature={quoteData.TermContractFeature}
               userRole={quoteData.user_role}
               employeeCount={quoteData.maxEECount}
               formType={formType}
               limitVolumeDiscount={quoteData.LimitVolumeDiscount}
            />
         </div>
         {quoteData.DifferenceQuote && (
            <div className='nobreak mb-5'>
               <PricingSection
                  sectionName={'Monthly Comparison'}
                  numColumns={4}
                  sectionHeaders={quoteData.DifferenceQuoteHeader}
                  lineItems={quoteData.DifferenceQuoteRows}
                  tableClass='tableClass2'
                  tableHeaderClass='tableHeader2'
                  targetColumnCount={undefined}
                  padColumnStyle={undefined}
                  columnWidths={undefined}
               />
            </div>
         )}
         <PayrollServices
            HasPayroll={quoteData.HasPayrollSelected}
            className='additionalPayrollServices fullWidth nobreak'
            currencyObject={quoteData.currencyObjectUSD}
         />
         <EORServices
            HasEOR={quoteData.HasEOR}
            className={
               'additionalPayrollServices fullWidth nobreak' +
               (quoteData.HasPayrollSelected ? ' mt-3' : '')
            }
            currencyObject={quoteData.currencyObject}
            bookId={quoteData.pricebookId}
         />
         <ACAServices
            HasBenAdmin={quoteData.HasBenAdminSelected}
            className={
               'additionalPayrollServices fullWidth nobreak' +
               (quoteData.HasEOR || quoteData.HasPayrollSelected ? ' mt-3' : '')
            }
         />
         <HRISServices
            hasCore={
               quoteData.ProductCoreEECount > 0 && quoteData.CoreProductSelected
            }
            className={
               'additionalPayrollServices fullWidth nobreak' +
               (quoteData.HasEOR || quoteData.HasPayrollSelected ? ' mt-3' : '')
            }
         />
         {(quoteData.HasMonthlyServices ||
            quoteData.ConsultingServices.length > 0) && (
            <div className='fullWidth'>
               <div className='sectionTitle'>
                  <span className='sectionLabel'>Product&nbsp;Details</span>
                  <hr className='tableLine' />
               </div>
               <ProductDescriptions
                  product_list={quoteData.product_list}
                  coreProductSelected={quoteData.CoreProductSelected}
                  consultingServices={quoteData.ConsultingServices}
                  hasCore={
                     quoteData.ProductCoreEECount > 0 &&
                     quoteData.CoreProductSelected
                  }
                  hasPayroll={quoteData.HasPayrollSelected}
                  hasBenAdmin={
                     quoteData.ProductBenefitsAdministrationEECount > 0
                  }
                  hasTimeTracking={quoteData.ProductTimeTrackingEECount > 0}
                  hasTimeAttendance={
                     quoteData.ProductTimeAttendanceEECount > 0 &&
                     quoteData.TimeProductSelected === 'timeAttendance'
                  }
                  hasTimeClocks={
                     quoteData.HasTimeClocks &&
                     quoteData.ProductTimeClockDetails.reduce(
                        (acc: number, clock: timeClockDetail) =>
                           acc + (clock.timeClockCode ? clock.count : 0),
                        0,
                     ) > 0
                  }
                  quoteType={quoteData.QuoteType}
                  useAltDescription={quoteData.UseAltDescription}
                  dynamicProducts={quoteData.DynamicProducts}
               />
            </div>
         )}
         {(quoteData.ImpType !== 'selfSetup' ||
            quoteData.BenAdminImpType !== 'none' ||
            quoteData.PayrollImpType !== 'none') &&
            quoteData.QuoteType !== 'monthly' && (
               <div className='fullWidth mt-3'>
                  <ImpDescriptions
                     product_list={quoteData.product_list}
                     coreProductSelected={quoteData.CoreProductSelected}
                     impType={quoteData.ImpType}
                     benAdminImpType={quoteData.BenAdminImpType}
                     payrollImpType={quoteData.PayrollImpType}
                     quoteType={quoteData.QuoteType}
                     dynamicProducts={quoteData.DynamicProducts}
                  />
                  <p className='productDescription'>
                     <i>
                        Implementation does not include the following services:
                        Adjustment of employee self-service and access
                        release/modification; Uploading of files, photos, and
                        documents; Manual data entry (hand entering) from paper
                        files or PDF documents; Setting up of
                        onboarding/offboarding checklists; Posting jobs or
                        importing resumes; Importing any Time Off data prior to
                        the current calendar year; Importing Benefit data in
                        excess of 1 calendar year and for any inactive
                        employees; Importing of any terminated employee data
                        prior to the current calendar year; Extended time longer
                        than the{' '}
                        {quoteData.CoreProductSelected === 'core3'
                           ? '90-day'
                           : 'dedicated 4-6 week'}{' '}
                        timeframe. - Extra time, if needed, is billed by the
                        hour at the conclusion of the implementation timeframe
                     </i>
                  </p>
               </div>
            )}
         <div className='productDescription nobreak mt-3'>
            <p>
               <i>
                  BambooHR will not do custom programming efforts or any
                  development work with the BambooHR API in integrating with
                  external systems. Utilization of the BambooHR API is completed
                  by the customer with technical support available via email.
                  Maintenance of integrated external systems is the sole
                  responsibility of the client. The enablement of BambooHR
                  Marketplace partners is also the responsibility of the client.
               </i>
            </p>
         </div>
         <div className='thingsYouNeedToKnow nobreak mt-3'>
            <ThingsYouNeedToKnow
               coreProductSelected={quoteData.CoreProductSelected}
               productCoreEECount={quoteData.ProductCoreEECount}
               hasPayroll={quoteData.HasPayrollSelected}
               hasTimeTracking={quoteData.ProductTimeTrackingEECount > 0}
               hasTimeAttendance={quoteData.ProductTimeAttendanceEECount > 0}
               timeProductSelected={
                  quoteData.TimeProductSelected ?? 'timeTracking'
               }
               pricebookID={quoteData.pricebookId}
               showPrepaymentDisclaimer={quoteData.MonthsSelected > 1}
               currencyObject={quoteData.currencyObject}
               product_list={quoteData.product_list}
               currencyObjectUSD={quoteData.currencyObjectUSD}
               hasEORGeo2={
                  (quoteData?.DynamicProducts?.has('EORGeo2') ?? false) &&
                  (quoteData?.DynamicProducts?.get('EORGeo2') ?? 0) > 0
               }
               formType={quoteData.formType}
               hasManagedOrConsultingService={
                  quoteData.HasManagedOrConsultingService
               }
            />
            <div
               className='footer'
               style={{
                  color: '#aaa',
                  fontSize: '13pt',
                  width: '100%',
                  textAlign: 'center',
               }}
            >
               BambooHR | 42 Future Way | Draper | Utah | 84020
            </div>
         </div>
      </div>
   )
}
